/**
 * Image utility functions for handling fallback images
 */

import axiosInstance from "../config/axiosApi/axiosApi";

// Fallback image path - Restaurant food placeholder image
export const FALLBACK_IMAGE = "/assets/img/placehoderimage.png";

/** Placeholder when a drinks-department item has no feature image */
export const DRINKS_FALLBACK_IMAGE = "/assets/img/drinks-placeholder.svg";

/**
 * True when category department is Drinks (case-insensitive).
 * @param {string|null|undefined} departmentName
 * @returns {boolean}
 */
export function isDrinksDepartment(departmentName) {
    return String(departmentName || "").trim().toLowerCase() === "drinks";
}

/**
 * True when a category/header name looks like drinks (fallback when department is unset).
 * @param {string|null|undefined} categoryName
 * @returns {boolean}
 */
export function isDrinksCategoryName(categoryName) {
    const n = String(categoryName || "").trim().toLowerCase();
    if (!n) return false;
    return n === "drinks" || n === "drink" || n.includes("drink") || n.includes("beverage");
}

/**
 * True when an item name looks like a drink (specials / upsells without department).
 * @param {string|null|undefined} itemName
 * @returns {boolean}
 */
export function looksLikeDrinkItemName(itemName) {
    const n = String(itemName || "").trim().toLowerCase();
    if (!n) return false;
    return /\b(coffee|latte|cappuccino|espresso|americano|mocha|frappe|tea|juice|smoothie|shake|milkshake|cola|soda|water|beer|wine|cocktail|mocktail|lemonade|drink|beverage|spritz|tonic)\b/.test(
        n
    );
}

/**
 * True when department, category, or item name indicates drinks.
 * @param {string|null|undefined} departmentName
 * @param {string|null|undefined} categoryName
 * @param {string|null|undefined} itemName
 * @returns {boolean}
 */
export function isDrinksContext(departmentName, categoryName, itemName = null) {
    return (
        isDrinksDepartment(departmentName) ||
        isDrinksCategoryName(categoryName) ||
        looksLikeDrinkItemName(itemName)
    );
}

/**
 * Department/category-aware menu item placeholder.
 * @param {string|null|undefined} departmentName
 * @param {string|null|undefined} categoryName - optional name hint when department is missing
 * @param {string|null|undefined} itemName - optional item-name hint (specials)
 * @returns {string}
 */
export function getMenuItemFallbackImage(departmentName, categoryName = null, itemName = null) {
    return isDrinksContext(departmentName, categoryName, itemName)
        ? DRINKS_FALLBACK_IMAGE
        : FALLBACK_IMAGE;
}

/** True if src is the generic food placeholder (typo path included). */
export function isGenericFoodPlaceholderSrc(src) {
    const s = String(src || "").toLowerCase();
    return s.includes("placehoderimage") || (s.includes("placehoder") && !s.includes("drinks"));
}

const PRODUCTION_MEDIA_BASE = (
    process.env.REACT_APP_API_URL_Production || "https://possvc.serveiq.co.uk/"
).trim();

function getOriginFromBase(baseURL) {
    if (!baseURL) return "";
    try {
        return new URL(String(baseURL)).origin;
    } catch {
        return String(baseURL).replace(/\/+$/, "").replace(/\/api$/i, "");
    }
}

function isLocalHostUrl(url) {
    if (!url || typeof url !== "string") return false;
    try {
        const host = new URL(url).hostname;
        return host === "localhost" || host === "127.0.0.1" || host === "[::1]" || host === "::1";
    } catch {
        return /localhost|127\.0\.0\.1/i.test(url);
    }
}

/**
 * Media files are often only on production while the API points at localhost in development.
 * Prefer production origin whenever the active API host is local.
 */
export function getMediaOrigin() {
    const productionOrigin =
        getOriginFromBase(PRODUCTION_MEDIA_BASE) || "https://possvc.serveiq.co.uk";
    const apiBase =
        axiosInstance?.defaults?.baseURL ||
        process.env.REACT_APP_API_URL ||
        process.env.REACT_APP_API_URL_Production ||
        "";
    const apiOrigin = getOriginFromBase(apiBase);
    const isDev = process.env.REACT_APP_Environment === "development";

    if (isDev || (apiOrigin && isLocalHostUrl(apiOrigin))) {
        return productionOrigin;
    }

    return apiOrigin || productionOrigin;
}

/**
 * Resolve a menu/upload path to an absolute URL on the media host (not under /api).
 * Relative paths and localhost absolute URLs use production media in local/dev.
 * App-bundled `/assets/...` paths stay relative so the CRA public folder still works.
 * @param {string} imageUrl
 * @returns {string}
 */
export const resolveImageUrl = (imageUrl) => {
    if (!imageUrl || typeof imageUrl !== "string" || imageUrl.trim() === "") {
        return "";
    }

    const trimmed = imageUrl.trim();
    const mediaOrigin = getMediaOrigin();

    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
        if (isLocalHostUrl(trimmed)) {
            try {
                const parsed = new URL(trimmed);
                // Keep local webpack/public assets on the frontend origin.
                if (/^\/assets\//i.test(parsed.pathname)) {
                    return trimmed;
                }
                return `${mediaOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
            } catch {
                return trimmed;
            }
        }
        return trimmed;
    }

    if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
        return trimmed;
    }

    const normalizedPath = `/${trimmed.replace(/^\/+/, "")}`;
    // Frontend placeholders / static images live in OnlineOrdering public/, not on the API.
    if (/^\/assets\//i.test(normalizedPath)) {
        return normalizedPath;
    }

    return `${mediaOrigin}${normalizedPath}`;
};

/**
 * Handle image error by retrying on production media host, then fallback image.
 * @param {Event} event - The error event from img onError
 * @param {string} fallbackImage - Optional custom fallback image path
 */
export const handleImageError = (event, fallbackImage = FALLBACK_IMAGE) => {
    const img = event.target;
    if (!img) return;

    const currentSrc = img.currentSrc || img.src || "";
    if (!img.dataset.prodAttempted && currentSrc && isLocalHostUrl(currentSrc)) {
        try {
            const parsed = new URL(currentSrc);
            const productionOrigin =
                getOriginFromBase(PRODUCTION_MEDIA_BASE) || "https://possvc.serveiq.co.uk";
            const prodSrc = `${productionOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
            if (prodSrc !== currentSrc) {
                img.dataset.prodAttempted = "true";
                img.src = prodSrc;
                return;
            }
        } catch {
            // fall through to placeholder
        }
    }

    // Prevent infinite loop if fallback also fails
    if (img.src !== fallbackImage && !img.dataset.fallbackAttempted) {
        img.dataset.fallbackAttempted = "true";
        img.src = fallbackImage;
    }
};

/**
 * Get image source with fallback handling
 * @param {string} imageSrc - The original image source
 * @param {string} fallbackImage - Optional custom fallback image path
 * @returns {string} - The image source or fallback
 */
export const getImageSrc = (imageSrc, fallbackImage = FALLBACK_IMAGE) => {
    return imageSrc || fallbackImage;
};

const imageUtils = {
    FALLBACK_IMAGE,
    DRINKS_FALLBACK_IMAGE,
    isDrinksDepartment,
    isDrinksCategoryName,
    looksLikeDrinkItemName,
    isDrinksContext,
    getMenuItemFallbackImage,
    isGenericFoodPlaceholderSrc,
    handleImageError,
    getImageSrc,
    resolveImageUrl,
    getMediaOrigin,
};

export default imageUtils;
